import React, { createContext, ReactNode, useEffect, useReducer, useState } from 'react';
import { assign, cloneDeep, compact, differenceWith, eq, isEqual, rearg, values } from 'lodash';

import { Maybe } from 'models/types';
import { Card_t, Card_taxonomy } from 'models/Models.gen';
import { EMPTY_STRING, VERTICAL_LINE_SIGN } from 'common/utils/string';
import { flowList } from 'utils/fp';
import { COUNTRY, envUtil } from 'common/util';
import useSegment from 'hooks/useSegment';
import {
  cardToProductWithIndex as Segment_cardToProductWithIndex,
  productListViewed as Segment_productListViewed,
  productsSearched as Segment_productsSearched,
} from 'bindings/Segment.bs';
import { getCardLists as CardSearchResults_getCardLists } from 'components/library/CardSearchResults.bs';
import { ProductSearchType } from 'models/segment.model';
import { specialRegex } from 'utils/Regex';

export type DrainContextType<T> = {
  // Drain
  currentValue?: Maybe<DrainValue>;
  setCurrentValue: (drainValue: DrainValue) => void;
  // Filter
  filter?: Maybe<FilterCurrent>;
  prevFilter?: Maybe<FilterCurrent>;
  setPrevFilter: (filterValue: Maybe<FilterCurrent>) => void;
  setFilter: (filterValue: Maybe<FilterCurrent>) => void;
  hasFilter: () => boolean;
  appendFilter: (filterAppend: FilterCurrentElement, shouldApply?: boolean) => void;
  clearFilter: (parentCodename: string) => void;
  trackDrainFilterSection: (filter: FilterCurrent, search: string) => void;
  // Sorting
  setSortValue: (value: SortValue, prevValue: SortValue) => void;
  sort: SortValue;
  prevSort: SortValue;
  hasSort: () => boolean;
  // Search
  search?: Maybe<string>;
  setSearch: (search: string) => void;
  onSearchChange: (hasChange: boolean) => void;
  hasSearch: () => boolean;
  // View
  switchFilter: (options: FilterSwitchOptions) => void;
  currentType?: Maybe<DrainType>;
  isOpen: boolean;
  // Mobile/Tablet Pages
  page?: Maybe<FilterPage>;
  setPage: (page: FilterPage) => void;
  // Drain Applying
  initValue: (itemList: T[]) => DrainValue<T>;
  applyFilter: (drainValue: DrainValue<T>) => DrainValue<T>;
  applySort: (drainValue: DrainValue<T>) => DrainValue<T>;
  apply: boolean;
  restore: (drainValue: DrainValue<T>) => Maybe<DrainValue<T>>;
  // Search
  applySearch: (drainValue: DrainValue<T>) => Maybe<DrainValue<T>>;
};

export type FilterSwitchOptions = {
  apply?: Maybe<boolean>;
  clear?: Maybe<boolean>;
  close?: Maybe<boolean>;
  device?: Maybe<Device>;
  display?: Maybe<boolean>;
  currentFilter?: Maybe<FilterCurrent>;
  sorting?: {
    current?: Maybe<SortValue>;
    previous?: Maybe<SortValue>;
  };
};

export type DrainContextProviderProps = {
  children?: ReactNode;
  value: DrainContextType<Card_t>;
};

export type FilterPage = {
  code: string;
  parent?: Maybe<FilterPage>;
};

export type FilterCurrent = {
  elements: FilterCurrentElement[];
};

export type FilterCurrentElement = {
  codename: string;
  name: string;
  parent?: Maybe<FilterCurrentElement>;
};

export enum DrainType {
  Filter = 'filter',
  Sorting = 'sorting',
}

export enum SortValue {
  LiteralUp = 'a-z',
  LiteralDown = 'z-a',
  Featured = 'featured',
  NewArrivals = 'new_arrivals',
}

export const DEFAULT_FILTER_PAGE: FilterPage = {
  code: 'default',
  parent: void 0,
};

export const DEFAULT_FILTER_CURRENT: FilterCurrent = {
  elements: [],
};

export type DrainValue<T = Card_t> = {
  cardList: T[];
  current: T[];
  filter: FilterCurrent;
  sort?: SortValue;
};

export enum Device {
  Desktop = 'desktop',
  Mobile = 'mobile',
  Tablet = 'tablet',
}

export function uniqueCardList(list: Card_t[]): Card_t[] {
  return Object.values(list.reduce((acc: { [uid: string]: Card_t }, obj: Card_t) => ({ ...acc, [obj.uid]: obj }), {}));
}

/**
 * Returns SKU Presentation codename regarding the region
 * @todo: compose the unmapped liked value
 * @param codename
 */
function getPresentationCodename(codename: string): string {
  if (codename === 'location') {
    return {
      [COUNTRY.Australia]: 'locationAu',
      [COUNTRY.NewZealand]: 'locationNz',
      [COUNTRY.GreatBritain]: 'locationUk',
      [COUNTRY.UnitedStates]: 'locationUs',
    }[COUNTRY[envUtil.getCountry()]];
  } else {
    return codename;
  }
}

export function filterBy(filter: FilterCurrent) {
  return (codename: string): ((drainValue: DrainValue) => DrainValue<Card_t>) => {
    return ({ cardList, current, sort }: DrainValue): DrainValue<Card_t> => {
      // apply filter by codename of taxonomies
      const filterCardList = current.filter(({ presentation }) => {
        const filterCodenameList: string[] = filter.elements
          .filter(({ parent: { codename: cn } }) => cn === codename)
          .map(({ codename }) => codename);
        const presentationValueList =
          presentation && presentation[getPresentationCodename(codename)]
            ? presentation[getPresentationCodename(codename)].map(({ value: { codename } }: Card_taxonomy) => codename)
            : [];
        return filterCodenameList.filter((codename: string) => presentationValueList.includes(codename)).length > 0;
      });
      return {
        cardList,
        filter,
        current: uniqueCardList(filterCardList) || [],
        sort,
      };
    };
  };
}

export function filterCards(drainValue: DrainValue, filter: FilterCurrent): DrainValue {
  const defaultFilterValue = {
    ...drainValue,
    current: drainValue.cardList,
  };
  const filterList =
    filter.elements.length > 0
      ? filter.elements
          .map(({ parent: { codename } }: FilterCurrentElement) => codename)
          .filter((codename: string, index: number, list: string[]) => list.indexOf(codename) === index)
      : [];
  return filterList.length > 0
    ? flowList([...filterList.map(filterBy(filter))])(defaultFilterValue)
    : defaultFilterValue;
}

/**
 * @note: The Sort class is not static for the further improvement.
 *        Redefine the class as static in case of additional values absence
 */
class DrainSort {
  /**
   * Locale is controlled by the further localisation.
   * Helper: https://www.techonthenet.com/js/language_tags.php
   */
  locale: 'en-AU';

  /**
   * Empty drainValue.cardList || drainValue.current value
   */
  emptyCardList: [];

  literalUp(drainValue: DrainValue): DrainValue {
    return {
      ...drainValue,
      current:
        drainValue.current.sort((next: Card_t, prev: Card_t) => {
          const nextSortValue = DrainSort.literalCardValue(next);
          const prevSortValue = DrainSort.literalCardValue(prev);
          return nextSortValue.localeCompare(prevSortValue, this.locale, {
            ignorePunctuation: true,
          });
        }) || this.emptyCardList,
    };
  }

  literalDown(drainValue: DrainValue): DrainValue {
    return {
      ...drainValue,
      current: this.literalUp(drainValue).current.reverse() || this.emptyCardList,
    };
  }

  featured(drainValue: DrainValue): DrainValue {
    return drainValue;
  }

  newArrivals(drainValue: DrainValue): DrainValue {
    return {
      ...drainValue,
      current:
        drainValue.current.sort((prev: Card_t, next: Card_t) => {
          const prevSortValue: Maybe<string> = DrainSort.dateCardValue(prev);
          const nextSortValue: Maybe<string> = DrainSort.dateCardValue(next);

          return new Date(nextSortValue).getTime() - new Date(prevSortValue).getTime();
        }) || this.emptyCardList,
    };
  }

  private static literalCardValue(card: Card_t): string {
    return card ? card.name : void 0;
  }

  private static dateCardValue(card: Card_t): Maybe<string> {
    // @ts-ignore
    return card ? card.createdAt : void 0;
  }
}

export function sortCards(drainValue: DrainValue, sort: SortValue): DrainValue {
  const drainSort = new DrainSort();
  return {
    [SortValue.LiteralUp]: drainSort.literalUp,
    [SortValue.LiteralDown]: drainSort.literalDown,
    [SortValue.Featured]: drainSort.featured,
    [SortValue.NewArrivals]: drainSort.newArrivals,
  }[sort].call(drainSort, drainValue);
}

/**
 * @todo: stabilise the search RegExp condition
 * @param search
 */
export function searchCard(search): (card: Card_t) => boolean {
  function format(str: string): string {
    return str.replace(/\s/gi, EMPTY_STRING).replace(new RegExp(specialRegex, 'g'), EMPTY_STRING);
  }
  return ({ searchableTerms, similarTerms }: Card_t) => {
    const regExp = new RegExp(format(search), 'gi');
    return regExp.test(format(`${searchableTerms}${similarTerms}`));
  };
}

const DEFAULT_DRAIN_VALUE: DrainValue<Card_t> = {
  cardList: [],
  current: [],
  sort: SortValue.Featured, // @note: default sort value
  filter: {
    elements: [],
  },
};

export enum DrainReducer {
  Apply = 'apply',
}

export enum DrainSample {
  Sort = 'sort',
  Filter = 'filter',
}

export type DrainState<T extends Card_t> = {
  value: DrainValue<T>;
};

export type DrainAction<T extends Card_t> = {
  type: DrainReducer;
  value: DrainValue<T>;
};

function drainReducer(drain: DrainState<Card_t>, action: DrainAction<Card_t>) {
  switch (action.type) {
    case DrainReducer.Apply:
      return Object.assign({}, drain.value, action.value);
    default:
      return drain;
    // throw new Error();
  }
}

export const LOCAL_STORAGE_SORT_ITEM = 'drain-sort-value';

export const DrainContext = createContext<DrainContextType<Card_t>>({
  page: DEFAULT_FILTER_PAGE,
  isOpen: false,
  setPage: () => void 0,
  filter: Object.assign({}, DEFAULT_FILTER_CURRENT),
  applyFilter: () => void 0,
  prevFilter: Object.assign({}, DEFAULT_FILTER_CURRENT),
  setPrevFilter: () => void 0,
  setFilter: () => void 0,
  switchFilter: () => void 0,
  appendFilter: () => void 0,
  search: EMPTY_STRING,
  setSearch: () => void 0,
  onSearchChange: () => void 0,
  hasSearch: () => false,
  currentType: DrainType.Filter,
  apply: false,
  applySearch: () => void 0,
  hasFilter: () => false,
  setSortValue: () => void 0,
  sort: SortValue.Featured,
  prevSort: SortValue.Featured,
  applySort: () => void 0,
  hasSort: () => false,
  initValue: () => void 0,
  clearFilter: () => void 0,
  currentValue: void 0,
  trackDrainFilterSection: () => void 0,
  restore: () => void 0,
  setCurrentValue: (drainValue: DrainValue) => void 0,
});

export const DrainContextProvider = ({ children, value }: DrainContextProviderProps) => {
  const [initial, setInitial] = useState<boolean>(true);
  const [page, setPage] = useState<FilterPage>(DEFAULT_FILTER_PAGE);
  const [filter, setFilter] = useState<FilterCurrent>(Object.assign({}, DEFAULT_FILTER_CURRENT, value.filter));
  const [prevFilter, setPrevFilter] = useState<FilterCurrent>(Object.assign({}, DEFAULT_FILTER_CURRENT));
  const [sort, setSort] = useState<Maybe<SortValue>>(value.sort || SortValue.Featured); // @note: "Featured" is the default sorting value
  const [prevSort, setPrevSort] = useState<Maybe<SortValue>>(SortValue.Featured); // @note: "Featured" is the default sorting value
  const [search, setSearch] = useState(value.search || EMPTY_STRING);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [currentType, setCurrentType] = useState<DrainType>(DrainType.Filter);
  const [apply, setApply] = useState<boolean>(false);
  const [currentValue, setCurrentValue] = useState<DrainValue>(value.currentValue);
  const [isSearchChange, onSearchChange] = useState<boolean>(false);

  const { filterSortApplied, productsSearched } = useSegment();

  useEffect(() => {
    setInitial(false);
    if (differenceWith(values(filter), values(DEFAULT_FILTER_CURRENT), isEqual)) {
      const filterValue = filterCards(currentValue, filter);
      setCurrentValue(filterValue);
      filterSortApplied({
        has_filter: true,
        has_audience_filter:
          filter.elements.filter(({ parent: { codename } }: FilterCurrentElement) => codename === 'audience').length >
          0,
        has_interests_filter:
          filter.elements.filter(({ parent: { codename } }: FilterCurrentElement) => codename === 'interest').length >
          0,
        has_location_filter:
          filter.elements.filter(({ parent: { codename } }: FilterCurrentElement) => codename === 'location').length >
          0,
        has_occasion_filter:
          filter.elements.filter(({ parent: { codename } }: FilterCurrentElement) => codename === 'occasion').length >
          0,
        has_search: search !== EMPTY_STRING,
        has_sort: sort !== SortValue.Featured,
        audience_filter_tags: filter.elements
          .filter(({ parent: { codename } }: FilterCurrentElement) => codename === 'audience')
          .map(({ codename }: FilterCurrentElement) => codename)
          .join(VERTICAL_LINE_SIGN),
        interests_filter_tags: filter.elements
          .filter(({ parent: { codename } }: FilterCurrentElement) => codename === 'interest')
          .map(({ codename }: FilterCurrentElement) => codename)
          .join(VERTICAL_LINE_SIGN),
        location_filter_tags: filter.elements
          .filter(({ parent: { codename } }: FilterCurrentElement) => codename === 'location')
          .map(({ codename }: FilterCurrentElement) => codename)
          .join(VERTICAL_LINE_SIGN),
        occasion_filter_tags: filter.elements
          .filter(({ parent: { codename } }: FilterCurrentElement) => codename === 'occasion')
          .map(({ codename }: FilterCurrentElement) => codename)
          .join(VERTICAL_LINE_SIGN),
        current_sort_select: sort,
        previous_sort_select: SortValue.Featured,
      });
      trackDrainFilterSection(filter, search);
    }
  }, []);

  const [drain, dispatchDrain] = useReducer(drainReducer, DEFAULT_DRAIN_VALUE);

  // @note: default sort has no confirmed effect with the Segment
  // useEffect(() => {
  //   const sort = localStorage.getItem(LOCAL_STORAGE_SORT_ITEM);
  //   setSortValue(!isUndefined(sort) && !isEqual(sort, 'null')
  //     ? sort as SortValue
  //     : SortValue.Featured
  //   );
  // }, []);

  /**
   * Composes the Drain Value from the card list
   * @param cardList
   */
  const initValue = (cardList: Card_t[]): DrainValue<Card_t> => {
    const currentDrainValue = {
      cardList,
      current: cardList,
      sort,
      filter,
    };
    // setCurrentValue(currentDrainValue);
    // dispatchDrain({
    //   type: DrainReducer.Apply,
    //   value: currentDrainValue,
    // });
    return currentDrainValue;
  };

  function switchFilter({
    clear = false,
    apply = false,
    close = false,
    sorting = {
      current: SortValue.Featured,
      previous: SortValue.Featured,
    },
    currentFilter = DEFAULT_FILTER_CURRENT,
    display = true,
    device = Device.Mobile,
  }: FilterSwitchOptions): void {
    if (clear === true) {
      setFilter(() => DEFAULT_FILTER_CURRENT);
    }
    if (close === true) {
      setFilter(prevFilter);
    } else {
      setPrevFilter(filter);
    }
    setPage(() => DEFAULT_FILTER_PAGE);
    setApply(() => apply);
    if (apply === true) {
      const filterValue = isEqual(currentFilter, DEFAULT_FILTER_CURRENT) ? filter : currentFilter;
      filterSortApplied({
        has_filter: eq(clear, true) ? false : filterValue.elements.length > 0,
        has_audience_filter: !clear
          ? filterValue.elements.filter(({ parent: { codename } }: FilterCurrentElement) => codename === 'audience')
              .length > 0
          : false,
        has_interests_filter: !clear
          ? filterValue.elements.filter(({ parent: { codename } }: FilterCurrentElement) => codename === 'interest')
              .length > 0
          : false,
        has_location_filter: !clear
          ? filterValue.elements.filter(({ parent: { codename } }: FilterCurrentElement) => codename === 'location')
              .length > 0
          : false,
        has_occasion_filter: !clear
          ? filterValue.elements.filter(({ parent: { codename } }: FilterCurrentElement) => codename === 'occasion')
              .length > 0
          : false,
        has_search: search !== EMPTY_STRING, // @todo: search integration
        has_sort: sorting.current !== SortValue.Featured,
        audience_filter_tags: !clear
          ? filterValue.elements
              .filter(({ parent: { codename } }: FilterCurrentElement) => codename === 'audience')
              .map(({ codename }: FilterCurrentElement) => codename)
              .join(VERTICAL_LINE_SIGN)
          : EMPTY_STRING,
        interests_filter_tags: !clear
          ? filterValue.elements
              .filter(({ parent: { codename } }: FilterCurrentElement) => codename === 'interest')
              .map(({ codename }: FilterCurrentElement) => codename)
              .join(VERTICAL_LINE_SIGN)
          : EMPTY_STRING,
        location_filter_tags: !clear
          ? filterValue.elements
              .filter(({ parent: { codename } }: FilterCurrentElement) => codename === 'location')
              .map(({ codename }: FilterCurrentElement) => codename)
              .join(VERTICAL_LINE_SIGN)
          : EMPTY_STRING,
        occasion_filter_tags: !clear
          ? filterValue.elements
              .filter(({ parent: { codename } }: FilterCurrentElement) => codename === 'occasion')
              .map(({ codename }: FilterCurrentElement) => codename)
              .join(VERTICAL_LINE_SIGN)
          : EMPTY_STRING,
        current_sort_select: sorting.current,
        previous_sort_select: sorting.previous,
      });
    }
    setOpen(() => (device !== Device.Desktop ? (eq(display, false) ? false : !isOpen) : false));
  }

  const appendFilter = (filterAppend: FilterCurrentElement, shouldApply: boolean = false) => {
    const currentFilter = cloneDeep(filter);
    if (currentFilter.elements.map(({ codename }) => codename).includes(filterAppend.codename)) {
      setFilter(
        cloneDeep(
          assign(
            {},
            {
              elements: [...currentFilter.elements.filter(({ codename }) => codename !== filterAppend.codename)],
            }
          )
        )
      );
    } else {
      setFilter(
        cloneDeep(
          assign(
            {},
            {
              elements: compact([...currentFilter.elements, filterAppend]),
            }
          )
        )
      );
    }
    if (shouldApply) {
      switchFilter({
        apply: true,
        close: false,
        device: Device.Desktop,
      });
      trackDrainFilterSection(filter, search);
    }
  };

  function clearFilter(parentCodename: string): void {
    setFilter({
      elements: filter.elements
        .map((item: FilterCurrentElement) => (item.parent.codename === parentCodename ? void 0 : item))
        .filter((item: Maybe<FilterCurrentElement>) => item !== void 0),
    });
  }

  function hasFilter(): boolean {
    return filter.elements.length > 0;
  }

  function setSortValue(value: SortValue, prevValue: SortValue): void {
    // TODO: remove when `localStorage.getItem(LOCAL_STORAGE_SORT_ITEM)` is confirmed can be removed
    // localStorage.setItem(LOCAL_STORAGE_SORT_ITEM, value);
    setPrevSort(prevValue);
    setSort(value);
  }

  function applyFilter(drainValue: DrainValue<Card_t>): DrainValue {
    // setApply(false);
    if (hasFilter()) {
      const value = filterCards(drainValue, filter);
      setCurrentValue(value);
      // dispatchDrain({
      //   type: DrainReducer.Apply,
      //   value,
      // });
      return value;
    } else {
      return drainValue;
    }
  }

  function applySort(drainValue: DrainValue): DrainValue {
    // setApply(false);
    if (hasSort()) {
      const value = sortCards(drainValue, sort);
      // dispatchDrain({
      //   type: DrainReducer.Apply,
      //   value,
      // });
      return value;
    } else {
      return drainValue;
    }
  }

  function hasSort(): boolean {
    return sort !== void 0 && sort !== SortValue.Featured;
  }

  function restore(drainValue: DrainValue<Card_t>): DrainValue<Card_t> {
    setApply(false);
    return drainValue;
  }

  function hasSearch(): boolean {
    // @note: apply search independently the search existing value
    return isSearchChange ? true : !isEqual(search, EMPTY_STRING);
  }

  function applySearch(drainValue: DrainValue): DrainValue {
    if (hasSearch()) {
      const value = {
        ...drainValue,
        current: drainValue.current.filter(searchCard(search)),
      };
      setCurrentValue(value);
      return value;
    } else {
      return drainValue;
    }
  }

  function trackDrainFilterSection(filter: FilterCurrent, search: string) {
    const filterDrainValue = filterCards(currentValue, filter);
    const cardDrainValue = {
      ...filterDrainValue,
      current: filterDrainValue.current.filter(searchCard(search)),
    };
    const products = cardDrainValue.current
      .filter(({ isSwappable }) => !isSwappable)
      .map(rearg(Segment_cardToProductWithIndex, [1, 0]));
    productsSearched({
      query: search,
      search_count: cardDrainValue.current.length,
      products,
      search_location: 'store',
      search_type:
        search && hasFilter()
          ? ProductSearchType.Both
          : hasFilter()
          ? ProductSearchType.Filter
          : ProductSearchType.Keyword,
    });
    Segment_productListViewed({
      list_id: 'catalogue-promo',
      category: 'Featured',
      products,
    });
  }

  return (
    <DrainContext.Provider
      value={{
        filter,
        prevFilter,
        isOpen,
        switchFilter,
        appendFilter,
        setPrevFilter,
        setFilter,
        page,
        setPage,
        search,
        setSearch,
        hasSearch,
        currentType,
        apply,
        initValue,
        applyFilter,
        hasFilter,
        clearFilter,
        setSortValue,
        sort,
        prevSort,
        applySort,
        hasSort,
        restore,
        applySearch,
        currentValue,
        onSearchChange,
        trackDrainFilterSection,
        setCurrentValue,
      }}
    >
      {children}
    </DrainContext.Provider>
  );
};
